import { IconButton } from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles, styled } from "@material-ui/styles";
import { Linked, useCollection, useIO, useLinked, useModel } from "@type-r/react";
import { CollapsableSection, Column, Flex, Loading, asDateTime, colors, div } from "lqx-react";
import React, { useEffect } from "react";
import { AssetEvent, AssetEventType, fetchIncomingAssetEvents } from "../api/assetEvent";
import { AssetType } from "../api/assetType";
import { CreditNoteData } from "../api/creditNoteChange";
import { CreditNoteCount } from "../api/creditNoteCount";
import { ImportJob } from "../api/importJob";
import { InvoiceData } from "../api/invoiceChange";
import { InvoiceCount } from "../api/invoiceCount";
import { MultipleAssetChange } from "../api/multipleAssetChange";
import { MultipleAssetsCount } from "../api/multipleAssetsCount";
import { PaymentData } from "../api/paymentChange";
import { PaymentCount } from "../api/paymentCount";
import { RemittanceAdviceData } from "../api/remittanceAdviceChange";
import { RemittanceAdviceCount } from "../api/remittanceAdviceCount";
import { UnappliedCashChange, UnappliedCashCount } from "../api/unappliedCash";
import { HeaderTitle } from "../components/asset-details-pane-styles";
import { CopyRow } from "../components/copy-row";
import { DetailsPaneContainer, DetailsPaneHeader, DetailsPaneStatus, DetailsPaneTab, DetailsPaneTabContent, DetailsPaneTabs } from "../components/item-card-styles";
import { ImportJobActions } from "./import-job-actions";
import { ImportJobStatusType } from "./models/ImportJobStatusType";

export interface AssetSentResult {
    successfulSent: number,
    unsuccessfulSent: number
}
export interface ProductModules {
    supplyChainFinance: AssetSentResult,
    sentForApproval: AssetSentResult,
    sentForReconciliation: AssetSentResult,
    sentToPostTradeSettlement: AssetSentResult
}
export const ImportJobDetailsPane = ({
    $doProcessJob,
    $activeCard,
    importJobCards
}: {
    $doProcessJob: Linked<{ jobId?: Number, needApproval?: boolean, actionType?: String, assetTypeId?: Number }>,
    $activeCard: Linked<ImportJob>,
    importJobCards?
}) => {
    const $selectedTab = useLinked('details');
    const classes = useStyles();

    const is = (assetType: AssetType) => {
        return $selectedTab.value === 'details' && Boolean($activeCard.value) && $activeCard.value.assetType == assetType;
    }

    useEffect(() =>
        $activeCard.set(importJobCards.find(i => i.jobId === $activeCard.value.jobId)), [importJobCards])

    return (
        <>
            <DetailsPaneContainer>
                <DetailsPaneHeader>
                    <IconButton aria-label="Close" size="small" className={classes.iconButton} onClick={() => $activeCard.set(null)}>
                        <CloseIcon />
                    </IconButton>
                    <Flex alignCenter>
                        <DetailsPaneStatus style={{ color: $activeCard.value.statusId.name === ImportJobStatusType.Processed ? colors.blue : colors.red }}>{$activeCard.value.statusId.name}</DetailsPaneStatus>
                    </Flex>
                    <HeaderTitle><>Job {$activeCard.value.jobId}</></HeaderTitle>
                </DetailsPaneHeader>
                <ImportJobActionContainer>
                    <ImportJobActions $doProcessJob={$doProcessJob} importJob={$activeCard.value}></ImportJobActions>
                </ImportJobActionContainer>
                <DetailsPaneTabs value={$selectedTab.value}
                    variant="scrollable"
                    scrollButtons="auto"
                    onChange={(_, newValue) => $selectedTab.set(newValue)} >
                    <DetailsPaneTab value="details" label=" Details" />
                </DetailsPaneTabs>
                <DetailsPaneTabContent>
                    {is(AssetType.invoice) && <Details assetChangesModel={InvoiceData} assetCountModel={InvoiceCount} importJob={$activeCard.value} classes={classes} />}
                    {is(AssetType.payment) && <Details assetChangesModel={PaymentData} assetCountModel={PaymentCount} importJob={$activeCard.value} classes={classes} />}
                    {is(AssetType.creditNote) && <Details assetChangesModel={CreditNoteData} assetCountModel={CreditNoteCount} importJob={$activeCard.value} classes={classes} />}
                    {is(AssetType.remittanceAdvice) && <Details assetChangesModel={RemittanceAdviceData} assetCountModel={RemittanceAdviceCount} importJob={$activeCard.value} classes={classes} />}
                    {is(AssetType.multiAsset) && <Details assetChangesModel={MultipleAssetChange} assetCountModel={MultipleAssetsCount} importJob={$activeCard.value} classes={classes} />}
                    {is(AssetType.unappliedCash) && <Details assetChangesModel={UnappliedCashChange} assetCountModel={UnappliedCashCount} importJob={$activeCard.value} classes={classes} />}
                </DetailsPaneTabContent>
            </DetailsPaneContainer>
        </>
    )
};

const Details = ({ importJob, assetChangesModel, assetCountModel, classes }
    : { importJob: ImportJob, assetChangesModel, assetCountModel, classes }) => {
    const batchSize = 10000;
    const defaultContent = (value: any) => Boolean(value) ? value : '-';

    const incomingAssetEvents = useCollection.of(AssetEvent);
    //TODO: need to rewrite logic on API with fetching assets for MultipleAssetChange to the same as we have for rest of the asset types (InvoiceData...)
    const assetChanges = assetChangesModel === MultipleAssetChange ? useCollection.of(assetChangesModel) : useModel(assetChangesModel);
    const assetCounts = useModel(assetCountModel);
    const productModules = useLinked<ProductModules>({
        supplyChainFinance: {
            successfulSent: 0,
            unsuccessfulSent: 0
        },
        sentForApproval: {
            successfulSent: 0,
            unsuccessfulSent: 0
        },
        sentForReconciliation: {
            successfulSent: 0,
            unsuccessfulSent: 0
        },
        sentToPostTradeSettlement: {
            successfulSent: 0,
            unsuccessfulSent: 0
        }
    });

    const isReady = useIO(async () => {
        await assetChanges.fetch({ params: { jobId: importJob.documentId, take: batchSize } }).catch(e => console.error(e));
        await assetCounts.fetch({ params: { jobId: importJob.documentId, take: batchSize } }).catch(e => console.error(e));
    }, [importJob.jobId]);

    const isProductModulesReady = useIO(async () => {
        if (incomingAssetEvents.length > 0) {
            incomingAssetEvents.reset();
        }
        //TODO: need to rewrite logic on API with fetching assets for MultipleAssetChange to the same as we have for rest of the asset types (InvoiceData...)
        const changes = assetChangesModel === MultipleAssetChange ? assetChanges : assetChanges.items;
        await fetchIncomingAssetEvents(changes, incomingAssetEvents)
            .catch(e => console.error(e));
    }, [assetChanges['_changeToken']]);

    useEffect(() => {
        productModules.set(buildModuleResults());
    }, [incomingAssetEvents['_changeToken']])

    const buildModuleResults = () => {
        const sentModulesCount = (assetType: AssetType, assetEventType: AssetEventType, success: boolean) => {
            let filtered = incomingAssetEvents
                .filter(_ => _.eventTypeId === assetEventType && _.success === success && _.assetTypeId === assetType);

            var uniqueArray = new Set<string>();
            filtered.forEach((element) => {
                uniqueArray.add(element.uid);
            });

            return uniqueArray.size;
        };

        const getAssetSentResult = (assetType: AssetType, assetEventType: AssetEventType) => {
            let assetSentResult: AssetSentResult = {
                successfulSent: 0,
                unsuccessfulSent: 0
            };

            assetSentResult = {
                successfulSent: sentModulesCount(assetType, assetEventType, true),
                unsuccessfulSent: sentModulesCount(assetType, assetEventType, false)
            };

            return assetSentResult;
        };

        let result: ProductModules;
        if (importJob.assetType === AssetType.multiAsset) {
            result = {
                supplyChainFinance: getAssetSentResult(AssetType.invoice, AssetEventType.scf_ingestor),
                sentForApproval: getAssetSentResult(AssetType.invoice, AssetEventType.inblock_mapper),
                sentForReconciliation: getAssetSentResult(AssetType.creditNote, AssetEventType.inblock_mapper),
                sentToPostTradeSettlement: getAssetSentResult(AssetType.remittanceAdvice, AssetEventType.pts_ingestor)
            };
        } else {
            result = {
                supplyChainFinance: getAssetSentResult(AssetType.invoice, AssetEventType.scf_ingestor),
                sentForApproval: getAssetSentResult(AssetType.invoice, AssetEventType.inblock_mapper),
                sentForReconciliation: getAssetSentResult(importJob.assetType, AssetEventType.inblock_mapper), /*for case when import job is payment assetType we should count payments assets, 
                when import job is creditNote assetType we should count credit note assets for reconciliation*/
                sentToPostTradeSettlement: getAssetSentResult(AssetType.remittanceAdvice, AssetEventType.pts_ingestor)
            };
        }
        return result;
    }

    return (
        <>
            <CollapsableSection title="General" titleClassName={classes.sectionTitle} noBorder noShadow >
                <Column>
                    <CopyRow label="File Name" value={defaultContent(importJob.originalFileName)} />
                    <CopyRow label="Asset Type" value={defaultContent(importJob.assetTypeNames)} />
                    <CopyRow label="Job ID" value={defaultContent(importJob.jobId)} />
                    <CopyRow label="Document ID" value={defaultContent(importJob.documentId)} />
                    <CopyRow label="Connection ID" value={defaultContent(importJob.connectionId)} hyperlinkHref={`/connections/edit/${importJob.connectionId}`} />
                    <CopyRow label="Import Template" value={defaultContent(importJob.importTemplateName)} hyperlinkHref={`/import-templates/edit/${importJob.importTemplateId}`} />
                    <CopyRow label="Created By" value={defaultContent(importJob.createdBy)} />
                    <CopyRow label="Created On" value={asDateTime(importJob.initiatedOn)} />
                </Column>
            </CollapsableSection>
            {
                importJob.statusId.name === ImportJobStatusType.Processed &&
                <>
                    <CollapsableSection title="Parse Results" titleClassName={classes.sectionTitle} noBorder noShadow>
                        {isReady && isProductModulesReady ?
                            <>
                                <RowItems>
                                    <RowItem>
                                        <Label>Total Assets</Label>
                                        <Content>{defaultContent(assetCounts.total)}</Content>
                                    </RowItem>
                                    <RowItem>
                                        <PassValidationLabel>Pass Validation</PassValidationLabel>
                                        <Content>{defaultContent(assetCounts.passValidationNewAsset + assetCounts.passValidationModifiedAsset)}</Content>
                                    </RowItem>
                                    <RowItem>
                                        <FailValidationLabel>Fail Validation</FailValidationLabel>
                                        <Content>{defaultContent(assetCounts.requireReview + assetCounts.duplicated + assetCounts.conflict)}</Content>
                                    </RowItem>
                                    <RowItem>
                                        <DigitizedLabel>Digitized</DigitizedLabel>
                                        <Content>{defaultContent(assetCounts.digitized)}</Content>
                                    </RowItem>
                                    <RowItem>
                                        <Label>Pending</Label>
                                        <Content>{defaultContent(assetCounts.total - assetCounts.digitized)}</Content>
                                    </RowItem>
                                </RowItems>
                            </> :
                            <Loading />
                        }
                    </CollapsableSection>
                    <CollapsableSection title="Product Modules" titleClassName={classes.sectionTitle} noBorder noShadow>
                        {isReady && isProductModulesReady ?
                            <>
                                <RowItems>
                                    <RowItem>
                                        <Label>Supply Chain Finance</Label>
                                        <CountSuccessBlock>
                                            <IconSuccess />
                                            <CountContent>{defaultContent(productModules.value.supplyChainFinance.successfulSent)}</CountContent>
                                        </CountSuccessBlock>
                                        <CountWarningBlock>
                                            <IconWarning />
                                            <CountContent>{defaultContent(productModules.value.supplyChainFinance.unsuccessfulSent)}</CountContent>
                                        </CountWarningBlock>
                                    </RowItem>
                                    <RowItem>
                                        <Label>Sent for Approval</Label>
                                        <CountSuccessBlock>
                                            <IconSuccess />
                                            <CountContent>{defaultContent(productModules.value.sentForApproval.successfulSent)}</CountContent>
                                        </CountSuccessBlock>
                                        <CountWarningBlock>
                                            <IconWarning />
                                            <CountContent>{defaultContent(productModules.value.sentForApproval.unsuccessfulSent)}</CountContent>
                                        </CountWarningBlock>
                                    </RowItem>
                                    <RowItem>
                                        <Label>Sent for Reconciliation</Label>
                                        <CountSuccessBlock>
                                            <IconSuccess />
                                            <CountContent>{defaultContent(productModules.value.sentForReconciliation.successfulSent)}</CountContent>
                                        </CountSuccessBlock>
                                        <CountWarningBlock>
                                            <IconWarning />
                                            <CountContent>{defaultContent(productModules.value.sentForReconciliation.unsuccessfulSent)}</CountContent>
                                        </CountWarningBlock>
                                    </RowItem>
                                    <RowItem>
                                        <Label>Sent to Post Trade Settlement</Label>
                                        <CountSuccessBlock>
                                            <IconSuccess />
                                            <CountContent>{defaultContent(productModules.value.sentToPostTradeSettlement.successfulSent)}</CountContent>
                                        </CountSuccessBlock>
                                        <CountWarningBlock>
                                            <IconWarning />
                                            <CountContent>{defaultContent(productModules.value.sentToPostTradeSettlement.unsuccessfulSent)}</CountContent>
                                        </CountWarningBlock>
                                    </RowItem>
                                </RowItems>
                            </> :
                            <Loading />
                        }
                    </CollapsableSection>
                </>
            }
        </>
    );
}

const IconSuccess = styled(CheckIcon)({
    color: '#4197d6',
    paddingRight: '3px'
});

const IconWarning = styled(WarningIcon)({
    color: '#eabc5e',
    paddingRight: '3px'
});

const CountSuccessBlock = div({
    display: 'inline',
    width: '60px',
    flexDirection: 'column',
});

const CountWarningBlock = div({
    display: 'inline',
    width: '60px',
    flexDirection: 'column',
});

const CountContent = div({
    display: 'inline',
    fontWeight: 'normal',
    fontSize: '12px',
    color: '#3E4B5F'
});

const RowItems = div({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    flex: '1 1 auto'
});

const RowItem = div({
    display: 'flex',
    flexDirection: 'row'
});

const ImportJobActionContainer = div({
    paddingRight: '30px'
});

const useStyles = makeStyles({
    iconButton: {
        display: 'block',
        marginLeft: 'auto',
        marginTop: '10px',
        marginRight: '10px',
    },
    sectionTitle: {
        color: '#63a9dc',
        fontSize: '13px',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    }
});

const Content = div({
    fontWeight: 'normal',
    fontSize: '12px',
    color: '#3E4B5F'
});

const Label = styled(Content)({
    flex: '1 1 auto',
    color: '#829AB0'
});

const PassValidationLabel = styled(Content)({
    flex: '1 1 auto',
    color: '#04d99d'
});

const DigitizedLabel = styled(Content)({
    flex: '1 1 auto',
    color: '#63a9dc'
});

const FailValidationLabel = styled(Content)({
    flex: '1 1 auto',
    color: '#f27324'
});
